import { createSlice, createAsyncThunk, SerializedError } from '@reduxjs/toolkit'
import { LoadingState, Redux } from '../..'
import { Permissions } from '@/src/types/apps/permissionTypes'
import { Scope } from '@/src/types/apps/resto365RoleScopes'
import { AxiosError } from 'axios'
import { Country } from '@/src/types/apps/resto365Countries'
import { Gyg365RestoList } from '@/src/types/apps/gyg365RestoListTypes'
import { HistoryLog } from '@/src/types/apps/historyLogTypes'
import { QueryParams } from '@/src/types/apps/queryParams'
import {
  FETCH_CURRENT_USER,
  FETCH_OKTA_USERS,
  FETCH_RESTO365_USERS,
  FETCH_RESTO365_USERS_COUNT,
  CREATE_RESTO365_USER,
  UPDATE_RESTO365_USER,
  UPDATE_USER_STATUS,
  FETCH_USER_TO_EDIT,
  FETCH_RESTO365_ROLES,
  CREATE_RESTO365_ROLE,
  UPDATE_RESTO365_ROLE,
  FETCH_ROLE_TO_EDIT,
  FETCH_ROLE_PERMISSIONS,
  FETCH_USER_AUDIT_LOGS,
  CHECK_ROLE_DUPLICATE,
  FETCH_RESTO365_AREAS,
  FETCH_RESTO365_RESTAURANTS_IN_AREA,
  FETCH_HISTORY_LOGS,
  EXPORT_HISTORY_LOGS,
  FILTER_RESTO365_USERS,
  FILTER_RESTO365_USERS_COUNT,
  RESTAURANT_ASSIGNMENT
} from './actions'

export interface OktaUser {
  id: string
  email: string
  name: string
  organization?: string
  status: string
}

export interface RestoUser {
  id: number
  email: string
  name: string
  phone: string
  department: string
  ownership: string
  role: RestoRole
  permissions: Permissions
  countries: {
    id: number
    name: string
  }[]
  areas: {
    id: number
    name: string
  }[]
  restaurants: Gyg365RestoList[] | { data: Gyg365RestoList[]; meta?: any }
  isActive?: boolean
  inactiveReason?: string
}

export interface RestoRole {
  id: number
  name: string
  isCustomRole: boolean
  scope: Scope
  permissions: Permissions
  handle?: string
  parentRoleId: number | undefined
  userCount: number
}

export interface AuditLog {
  id: number
  createdAt: string
  correlationId: string
  userId: number
  username: string
  email: string
  roleId: number
  roleName: string
  subject: string
  action: string
  origin: string
  entitySource: string
  entitySourceId: number
  module: string
  description: string
  requestedValue?: any
  initialValue?: any
  updatedValue?: any
}

export interface RestoArea {
  id: number
  name: string
  shortName: string
  country: {
    name: Country
  }
  restaurants: RestoRestaurant[]
}

export interface RestoRestaurant {
  id: number
  areaId: number
  ownership: string
  restaurantName: string
  franchiseId?: number | null
}

export interface UpdateResto365UserArgs {
  [key: string]: any
  id: number
  name?: string
  department?: string
  phone?: string | null
  roleId?: number
  countries?: Country[]
  areaIds?: number[] | null
  restaurantIds?: number[]
}

export interface CreateResto365UserArgs {
  ownership?: any
  oktaId: string
  email: string
  name: string
  department: string
  phone: string | null
  roleId: number
  countries: Country[]
  areaIds: number[] | null
  restaurantIds: number[]
  createFranchiseContact?: boolean
}

export interface CreateResto365RoleArgs {
  name: string
  permissions: Permissions
  scope: Scope
  parentRoleId?: number
}

type UpdateResto365RoleArgs = {
  id: number
  name: string
  scope: Scope
  parentRoleId?: number
  permissions: Permissions
}

interface RootState {
  oktaUsers: StateSlice<OktaUser[] | null>
  resto365Roles: StateSlice<RestoRole[]>
  resto365Areas: StateSlice<RestoArea[]>
  resto365Restaurants: StateSlice<RestoRestaurant[]>
  currentUser: StateSlice<RestoUser | null>
  createdUser: StateSlice<RestoUser | null>
  createdRole: StateSlice<RestoRole | null>
  updatingRole: StateSlice<RestoRole | null>
  updatedRole: StateSlice<RestoRole | null>
  resto365Users: StateSlice<RestoUser[]> & { count: number }
  updatingUser: StateSlice<RestoUser | null>
  updatedUser: StateSlice<RestoUser | null>
  roleDuplicateCheck: StateSlice<{ isDuplicate: boolean } | null>
  rolePermissions: StateSlice<any | null>
  userAuditLogs: StateSlice<AuditLog[]> & { meta: any }
  historyLogs: StateSlice<HistoryLog[]>
  historyLogsExport: StateSlice<HistoryLog[]>
  historyLogsMeta: {
    totalRows: number
    pages: number
  } | null
  allRestaurantScopeUsers: StateSlice<RestoUser[]>
}

export const ALL_ROLE_FILTER: RestoRole = {
  id: Number.MAX_SAFE_INTEGER,
  name: 'All roles',
  isCustomRole: false,
  scope: Scope.Country,
  permissions: {} as Permissions,
  parentRoleId: undefined,
  userCount: 0
}

type StateSlice<T> = {
  value: T
  loadingState: LoadingState
  error: SerializedError | null
}

type InitialStateType<K> = K extends StateSlice<infer T> ? T : never

const getInitialStateFor = <K>(initialState: InitialStateType<K>) => {
  return {
    value: initialState,
    loadingState: 'initial' as LoadingState,
    error: null
  }
}

const initialState: RootState = {
  oktaUsers: getInitialStateFor<RootState['oktaUsers']>(null),
  resto365Roles: getInitialStateFor<RootState['resto365Roles']>([]),
  resto365Areas: getInitialStateFor<RootState['resto365Areas']>([]),
  resto365Restaurants: getInitialStateFor<RootState['resto365Restaurants']>([]),
  currentUser: getInitialStateFor<RootState['currentUser']>(null),
  createdUser: getInitialStateFor<RootState['createdUser']>(null),
  createdRole: getInitialStateFor<RootState['createdRole']>(null),
  updatingRole: getInitialStateFor<RootState['updatingRole']>(null),
  updatedRole: getInitialStateFor<RootState['updatedRole']>(null),
  updatedUser: getInitialStateFor<RootState['updatedUser']>(null),
  updatingUser: getInitialStateFor<RootState['updatingUser']>(null),
  resto365Users: {
    value: [],
    loadingState: 'initial' as LoadingState,
    error: null,
    count: 0
  },
  roleDuplicateCheck: getInitialStateFor<RootState['roleDuplicateCheck']>(null),
  rolePermissions: getInitialStateFor<RootState['rolePermissions']>(null),
  userAuditLogs: {
    value: [],
    loadingState: 'initial' as LoadingState,
    error: null,
    meta: null
  },
  historyLogs: {
    value: [],
    loadingState: 'initial',
    error: null
  },
  historyLogsExport: {
    value: [],
    loadingState: 'initial',
    error: null
  },
  historyLogsMeta: null,
  allRestaurantScopeUsers: getInitialStateFor<RootState['allRestaurantScopeUsers']>([])
}

// Helper function to fetch role handle by role ID
const fetchRoleHandle = async (roleId: number, httpClient: any): Promise<string | undefined> => {
  try {
    const response = await httpClient.get(`resto365-role/fetch-role-handle/${roleId}`)
    const {
      data: { data: roleHandle }
    } = response

    return roleHandle
  } catch {
    console.warn(`Failed to fetch role handle for role ID ${roleId}`)

    return undefined
  }
}

export const fetchCurrentUser = createAsyncThunk<RestoUser | null, void, Redux>(
  FETCH_CURRENT_USER,
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient.get(`resto365-user/me`)
      const {
        data: { data: me }
      } = response

      // Fetch role handle if it's missing
      if (me && me.role && !me.role.handle && me.role.id) {
        const roleHandle = await fetchRoleHandle(me.role.id, extra.httpClient)
        if (roleHandle) {
          me.role.handle = roleHandle
        }
      }

      return me
    } catch {
      throw new Error('Login failed, please contact an Administrator.')
    }
  }
)

export const fetchOktaUsers = createAsyncThunk<OktaUser[] | null, string, Redux>(
  FETCH_OKTA_USERS,
  async (inputEmail: string, { extra }) => {
    try {
      const response = await extra.httpClient(`okta-user?q=${inputEmail}`)
      const {
        data: { data: oktaUsers }
      } = response

      return oktaUsers
    } catch {
      throw new Error('Failed to fetch Okta user')
    }
  }
)

export const fetchResto365Users = createAsyncThunk<RestoUser[], { roleId: number; name: string; page: number }, Redux>(
  FETCH_RESTO365_USERS,
  async ({ roleId, name, page }, { extra }) => {
    try {
      const params = new URLSearchParams()
      if (roleId !== ALL_ROLE_FILTER.id) {
        params.append('roleId', roleId.toString())
      }
      if (name) {
        params.append('name', name)
      }
      params.append('page', (page + 1).toString())

      const response = await extra.httpClient(`resto365-user?${params.toString()}`)
      const {
        data: { data: resto365Users }
      } = response

      return resto365Users
    } catch {
      throw new Error('Failed to fetch Resto365 users')
    }
  }
)

export const fetchResto365UsersCount = createAsyncThunk<number, { roleId: number; name: string }, Redux>(
  FETCH_RESTO365_USERS_COUNT,
  async ({ roleId, name }, { extra }) => {
    try {
      const params = new URLSearchParams()
      if (roleId !== ALL_ROLE_FILTER.id) {
        params.append('roleId', roleId.toString())
      }
      if (name) {
        params.append('name', name)
      }

      const response = await extra.httpClient(`resto365-user/count?${params.toString()}`)
      const {
        data: { data: count }
      } = response

      return count
    } catch {
      throw new Error('Failed to fetch Resto365 users')
    }
  }
)

export const fetchResto365Roles = createAsyncThunk<RestoRole[], void, Redux>(
  FETCH_RESTO365_ROLES,
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-role?sort=name&order=asc`)
      const {
        data: { data: resto365Roles }
      } = response

      return resto365Roles
    } catch {
      throw new Error('Failed to fetch Resto365 roles')
    }
  }
)

export const fetchResto365Areas = createAsyncThunk<RestoArea[], void, Redux>(
  FETCH_RESTO365_AREAS,
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-area`)
      const {
        data: { data: resto365Areas }
      }: { data: { data: RestoArea[] } } = response

      return resto365Areas
    } catch {
      throw new Error('Failed to fetch Resto365 Areas')
    }
  }
)

export const fetchResto365RestaurantsInArea = createAsyncThunk<RestoRestaurant[], number[], Redux>(
  FETCH_RESTO365_RESTAURANTS_IN_AREA,
  async (areaID: number[], { extra }) => {
    try {
      const responses = await Promise.all(areaID.map(id => extra.httpClient(`resto365-area/${id}?include=restaurants`)))

      const allRestaurants = responses.flatMap(response => {
        const {
          data: { data: resto365Area }
        }: { data: { data: RestoArea } } = response

        return resto365Area.restaurants
      })

      return allRestaurants
    } catch {
      throw new Error(`Failed to fetch Resto365 Restaurants in Area ${areaID}`)
    }
  }
)

export const createResto365User = createAsyncThunk<RestoUser | null, CreateResto365UserArgs, Redux>(
  CREATE_RESTO365_USER,
  async (userData, { extra }) => {
    try {
      const response = await extra.httpClient.post('resto365-user', userData)

      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to create user')
      }

      throw new Error('Failed to create user')
    }
  }
)

export const createResto365Role = createAsyncThunk<RestoRole | null, CreateResto365RoleArgs, Redux>(
  CREATE_RESTO365_ROLE,
  async (roleData, { extra }) => {
    try {
      const response = await extra.httpClient.post('resto365-role', roleData)

      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to create role')
      }
      throw new Error('Failed to create role')
    }
  }
)

export const fetchRoleToEdit = createAsyncThunk<RestoRole | null, number, Redux>(
  FETCH_ROLE_TO_EDIT,
  async (roleId, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-role/${roleId}`)
      const {
        data: { data: role }
      } = response

      return role
    } catch {
      throw new Error('Failed to fetch role to edit')
    }
  }
)

export const fetchRolePermissions = createAsyncThunk<any, number, Redux>(
  FETCH_ROLE_PERMISSIONS,
  async (roleId, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-role/${roleId}`)
      const {
        data: { data: rolePermissions }
      } = response

      return rolePermissions
    } catch {
      throw new Error('Failed to fetch role permissions')
    }
  }
)

export const fetchUserAuditLogs = createAsyncThunk<
  { data: AuditLog[]; meta: any },
  { userId: number; page?: number; pageSize?: number; dateFrom?: Date; dateTo?: Date; action?: string },
  Redux
>(FETCH_USER_AUDIT_LOGS, async ({ userId, page = 1, pageSize = 10, dateFrom, dateTo, action }, { extra }) => {
  try {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())

    // Add date range if provided
    if (dateFrom) {
      params.append('dateFrom', dateFrom.toISOString())
    }

    if (dateTo) {
      params.append('dateTo', dateTo.toISOString())
    }

    // Add action filter if provided
    if (action) {
      params.append('action', action)
    }

    const response = await extra.httpClient(`resto365-audit/user/${userId}?${params.toString()}`)
    const {
      data: { data: auditLogs, meta }
    } = response

    return { data: auditLogs, meta }
  } catch {
    throw new Error('Failed to fetch user audit logs')
  }
})

export const updateResto365Role = createAsyncThunk<RestoRole | null, UpdateResto365RoleArgs, Redux>(
  UPDATE_RESTO365_ROLE,
  async (roleData, { extra }) => {
    try {
      const response = await extra.httpClient.patch(`resto365-role/${roleData.id}`, roleData)

      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to update role')
      }
      throw new Error('Failed to update role')
    }
  }
)

export const fetchUserToEdit = createAsyncThunk<RestoUser | null, number, Redux>(
  FETCH_USER_TO_EDIT,
  async (userId, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-user/${userId}`)
      const {
        data: { data: user }
      } = response

      return user
    } catch {
      throw new Error('Failed to fetch user to edit')
    }
  }
)

export const updateResto365User = createAsyncThunk<RestoUser | null, UpdateResto365UserArgs, Redux>(
  UPDATE_RESTO365_USER,
  async (userData, { extra }) => {
    try {
      const response = await extra.httpClient.patch(`resto365-user/${userData.id}`, userData)

      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to update user')
      }
      throw new Error('Failed to update user')
    }
  }
)

// Update User Status
export const updateUserStatus = createAsyncThunk<
  RestoUser,
  { userId: number; isActive: boolean; inactiveReason?: string },
  Redux
>(UPDATE_USER_STATUS, async ({ userId, isActive, inactiveReason }, { extra }) => {
  try {
    const response = await extra.httpClient.patch(`resto365-user/${userId}/is-active`, {
      isActive,
      inactiveReason: inactiveReason || ''
    })

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to update user status')
    }
    throw new Error('Failed to update user status')
  }
})

// Link or Unlink User to/from Restaurant
export const restaurantAssignment = createAsyncThunk<
  { success: boolean; message: string },
  { userId: number; restaurantId: number; link: boolean; isPrimary?: boolean },
  Redux
>(RESTAURANT_ASSIGNMENT, async ({ userId, restaurantId, link, isPrimary }, { extra, rejectWithValue }) => {
  try {
    const payload: { userId: number; restaurantId: number; link: boolean; isPrimary?: boolean } = {
      userId,
      restaurantId,
      link
    }

    // Only include isPrimary if link is true (only relevant when linking)
    if (link && isPrimary !== undefined) {
      payload.isPrimary = isPrimary
    }

    const response = await extra.httpClient.post('resto365-user/restaurant-assignment', payload)

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      return rejectWithValue(e.response?.data?.message || 'Failed to link/unlink restaurant')
    }
    throw new Error('Failed to link/unlink restaurant')
  }
})

export const gyg365UserListed = createAsyncThunk<
  any,
  { roleId: number; name: string; ownership: string; page: number; areaID: number[] },
  Redux
>(FILTER_RESTO365_USERS, async ({ roleId, name, ownership, page, areaID }, { extra }) => {
  try {
    const params = new URLSearchParams()
    if (roleId !== ALL_ROLE_FILTER.id) {
      params.append('roleId', roleId.toString())
    }
    if (name) {
      params.append('name', name.toString())
    }
    if (ownership) {
      params.append('ownership', ownership.toString())
    }
    if (areaID) {
      params.append('areaId', areaID.join(','))
    }
    params.append('page', (page + 1).toString())

    const responseData = await extra.httpClient(`/resto365-user?${params.toString()}`)

    return responseData.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to filter user')
    }
    throw new Error('Failed to filter user')
  }
})

export const gyg365UserListedCount = createAsyncThunk<
  any,
  { roleId: number; name: string; ownership: string; areaID: number[] },
  Redux
>(FILTER_RESTO365_USERS_COUNT, async ({ roleId, name, ownership, areaID }, { extra }) => {
  try {
    const params = new URLSearchParams()
    if (roleId !== ALL_ROLE_FILTER.id) {
      params.append('roleId', roleId.toString())
    }
    if (name) {
      params.append('name', name.toString())
    }
    if (ownership) {
      params.append('ownership', ownership.toString())
    }
    if (areaID) {
      params.append('areaId', areaID.join(','))
    }

    const response = await extra.httpClient(`resto365-user/count?${params.toString()}`)
    const {
      data: { data: count }
    } = response

    return count
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to get user count')
    }
    throw new Error('Failed to get user count')
  }
})

export const checkRoleDuplicate = createAsyncThunk<{ isDuplicate: boolean }, string, Redux>(
  CHECK_ROLE_DUPLICATE,
  async (name, { extra }) => {
    try {
      const response = await extra.httpClient.get(`resto365-role/duplicate/${encodeURIComponent(name)}`)
      const { data } = response

      if (!data?.data || typeof data.data.isDuplicate !== 'boolean') {
        console.error('Invalid response structure:', data)
        throw new Error('Invalid response from server')
      }

      return { isDuplicate: data.data.isDuplicate }
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorResponse = {
          status: e.response?.status,
          statusText: e.response?.statusText,
          data: e.response?.data,
          message: e.message,
          config: {
            url: e.config?.url,
            method: e.config?.method,
            headers: e.config?.headers
          }
        }
        console.error('Role duplication check failed:', errorResponse)
        throw new Error(e.response?.data?.message || 'Failed to check role duplication')
      }
      console.error('Unexpected error during role duplication check:', e)
      throw new Error('Failed to check role duplication')
    }
  }
)

export const fetchHistoryLogs = createAsyncThunk<{ data: HistoryLog[]; meta: any }, QueryParams, Redux>(
  FETCH_HISTORY_LOGS,
  async (params, { extra }) => {
    try {
      const response = await extra.httpClient.get(`/history-logs`, { params })

      return response.data
    } catch (e) {
      throw new Error('Failed to fetch history logs')
    }
  }
)

export const exportHistoryLogs = createAsyncThunk<{ data: HistoryLog[] }, QueryParams, Redux>(
  EXPORT_HISTORY_LOGS,
  async (params, { extra }) => {
    try {
      const response = await extra.httpClient.get(`/history-logs/export`, { params })

      return response.data.data
    } catch (e) {
      throw new Error('Failed to export history logs')
    }
  }
)

export const fetchAllRestaurantScopeUsers = createAsyncThunk<RestoUser[], void, Redux>(
  'api/fetchAllRestaurantScopeUsers',
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient.get('/resto365-user/restaurant-scope')

      // Extract the data array from the response
      return response.data.data
    } catch (e) {
      console.error('Error fetching restaurant scope users:', e)
      throw new Error('Failed to fetch restaurant scope users')
    }
  }
)

const resto365UserManagement = createSlice({
  name: 'resto365UserManagement',
  initialState,
  reducers: {
    logout: state => {
      state.currentUser = initialState.currentUser
    },
    invalidateRoles: state => {
      state.resto365Roles = {
        ...initialState.resto365Roles,
        loadingState: 'invalidated'
      }
    },
    clearCreatedRole: state => {
      state.createdRole = initialState.createdRole
    },
    clearUpdatedRole: state => {
      state.updatedRole = initialState.updatedRole
    },
    clearUpdatingRole: state => {
      state.updatingRole = initialState.updatingRole
    },
    clearCreatedUser: state => {
      state.createdUser = initialState.createdUser
    },
    clearUpdatedUser: state => {
      state.updatedUser = initialState.updatedUser
    },
    clearUpdatingUser: state => {
      state.updatingUser = initialState.updatingUser
    },
    invalidateResto365Users: state => {
      state.resto365Users = {
        ...initialState.resto365Users,
        loadingState: 'invalidated'
      }
    },
    clearResto365Users: state => {
      state.resto365Users = {
        value: [],
        loadingState: 'initial' as LoadingState,
        error: null,
        count: 0
      }
    },
    clearOktaUsers: state => {
      state.oktaUsers = initialState.oktaUsers
    },
    clearRoleDuplicateCheck: state => {
      state.roleDuplicateCheck = initialState.roleDuplicateCheck
    },
    clearRolePermissions: state => {
      state.rolePermissions = initialState.rolePermissions
    },
    clearUserAuditLogs: state => {
      state.userAuditLogs = initialState.userAuditLogs
    },
    clearHistoryLogs: state => {
      state.historyLogs = initialState.historyLogs
      state.historyLogsMeta = null
    },
    clearHistoryLogsExport: state => {
      state.historyLogsExport = initialState.historyLogsExport
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOktaUsers.pending, state => {
        state.oktaUsers.loadingState = 'pending'
      })
      .addCase(fetchOktaUsers.fulfilled, (state, action) => {
        state.oktaUsers.loadingState = 'success'
        state.oktaUsers.value = action.payload
      })
      .addCase(fetchOktaUsers.rejected, (state, action) => {
        state.oktaUsers.loadingState = 'error'
        state.oktaUsers.error = action.error
      })
      .addCase(fetchResto365Roles.pending, state => {
        state.resto365Roles.loadingState = 'pending'
      })
      .addCase(fetchResto365Roles.fulfilled, (state, action) => {
        state.resto365Roles.loadingState = 'success'
        state.resto365Roles.value = action.payload
      })
      .addCase(fetchResto365Roles.rejected, (state, action) => {
        state.resto365Roles.loadingState = 'error'
        state.resto365Roles.error = action.error
      })
      .addCase(createResto365User.pending, state => {
        state.createdUser.loadingState = 'pending'
      })
      .addCase(createResto365User.fulfilled, (state, action) => {
        state.createdUser.loadingState = 'success'
        state.createdUser.value = action.payload
      })
      .addCase(createResto365User.rejected, (state, action) => {
        state.createdUser.loadingState = 'error'
        state.createdUser.error = action.error
      })
      .addCase(fetchCurrentUser.pending, state => {
        state.currentUser.loadingState = 'pending'
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUser.loadingState = 'success'
        state.currentUser.value = action.payload
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.currentUser.loadingState = 'error'
        state.currentUser.error = action.error
      })
      .addCase(fetchResto365Areas.pending, state => {
        state.resto365Areas.loadingState = 'pending'
      })
      .addCase(fetchResto365Areas.fulfilled, (state, action) => {
        state.resto365Areas.loadingState = 'success'
        state.resto365Areas.value = action.payload
      })
      .addCase(fetchResto365Areas.rejected, (state, action) => {
        state.resto365Areas.loadingState = 'error'
        state.resto365Areas.error = action.error
      })
      .addCase(fetchResto365RestaurantsInArea.pending, state => {
        state.resto365Restaurants.loadingState = 'pending'
      })
      .addCase(fetchResto365RestaurantsInArea.fulfilled, (state, action) => {
        state.resto365Restaurants.loadingState = 'success'
        state.resto365Restaurants.value = action.payload
      })
      .addCase(fetchResto365RestaurantsInArea.rejected, (state, action) => {
        state.resto365Restaurants.loadingState = 'error'
        state.resto365Restaurants.error = action.error
      })
      .addCase(fetchResto365Users.pending, state => {
        state.resto365Users.loadingState = 'pending'
      })
      .addCase(fetchResto365Users.fulfilled, (state, action) => {
        state.resto365Users.loadingState = 'success'
        state.resto365Users.value = action.payload
      })
      .addCase(fetchResto365Users.rejected, (state, action) => {
        state.resto365Users.loadingState = 'error'
        state.resto365Users.error = action.error
      })
      .addCase(fetchResto365UsersCount.pending, state => {
        state.resto365Users.loadingState = 'pending'
      })
      .addCase(fetchResto365UsersCount.fulfilled, (state, action) => {
        state.resto365Users.loadingState = 'success'
        state.resto365Users.count = action.payload || 0
      })
      .addCase(fetchResto365UsersCount.rejected, (state, action) => {
        state.resto365Users.loadingState = 'error'
        state.resto365Users.error = action.error
      })
      .addCase(createResto365Role.pending, state => {
        state.createdRole.loadingState = 'pending'
      })
      .addCase(createResto365Role.fulfilled, (state, action) => {
        state.createdRole.loadingState = 'success'
        state.createdRole.value = action.payload
      })
      .addCase(createResto365Role.rejected, (state, action) => {
        state.createdRole.loadingState = 'error'
        state.createdRole.error = action.error
      })
      .addCase(fetchRoleToEdit.pending, state => {
        state.updatingRole.loadingState = 'pending'
      })
      .addCase(fetchRoleToEdit.fulfilled, (state, action) => {
        state.updatingRole.loadingState = 'success'
        state.updatingRole.value = action.payload
      })
      .addCase(fetchRoleToEdit.rejected, (state, action) => {
        state.updatingRole.loadingState = 'error'
        state.updatingRole.error = action.error
      })
      .addCase(fetchRolePermissions.pending, state => {
        state.rolePermissions.loadingState = 'pending'
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.rolePermissions.loadingState = 'success'
        state.rolePermissions.value = action.payload
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.rolePermissions.loadingState = 'error'
        state.rolePermissions.error = action.error
      })
      .addCase(fetchUserAuditLogs.pending, state => {
        state.userAuditLogs.loadingState = 'pending'
      })
      .addCase(fetchUserAuditLogs.fulfilled, (state, action) => {
        state.userAuditLogs.loadingState = 'success'
        state.userAuditLogs.value = action.payload.data
        state.userAuditLogs.meta = action.payload.meta
      })
      .addCase(fetchUserAuditLogs.rejected, (state, action) => {
        state.userAuditLogs.loadingState = 'error'
        state.userAuditLogs.error = action.error
      })
      .addCase(updateResto365Role.pending, state => {
        state.updatedRole.loadingState = 'pending'
      })
      .addCase(updateResto365Role.fulfilled, (state, action) => {
        state.updatedRole.loadingState = 'success'
        state.updatedRole.value = action.payload
      })
      .addCase(updateResto365Role.rejected, (state, action) => {
        state.updatedRole.loadingState = 'error'
        state.updatedRole.error = action.error
      })
      .addCase(fetchUserToEdit.pending, state => {
        state.updatingUser.loadingState = 'pending'
      })
      .addCase(fetchUserToEdit.fulfilled, (state, action) => {
        state.updatingUser.loadingState = 'success'
        state.updatingUser.value = action.payload
      })
      .addCase(fetchUserToEdit.rejected, (state, action) => {
        state.updatingUser.loadingState = 'error'
        state.updatingUser.error = action.error
      })
      .addCase(updateResto365User.pending, state => {
        state.updatedUser.loadingState = 'pending'
      })
      .addCase(updateResto365User.fulfilled, (state, action) => {
        state.updatedUser.loadingState = 'success'
        state.updatedUser.value = action.payload
      })
      .addCase(updateResto365User.rejected, (state, action) => {
        state.updatedUser.loadingState = 'error'
        state.updatedUser.error = action.error
      })
      .addCase(updateUserStatus.pending, state => {
        state.updatedUser.loadingState = 'pending'
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.updatedUser.loadingState = 'success'
        state.updatedUser.value = action.payload

        // Update the user in the users list if it exists
        const userIndex = state.resto365Users.value.findIndex((user: RestoUser) => user.id === action.payload.id)
        if (userIndex !== -1) {
          // Merge the updated fields with the existing user data to preserve all properties
          state.resto365Users.value[userIndex] = {
            ...state.resto365Users.value[userIndex],
            ...action.payload
          }
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.updatedUser.loadingState = 'error'
        state.updatedUser.error = action.error
      })
      .addCase(gyg365UserListed.fulfilled, (state, action) => {
        state.resto365Users.loadingState = 'success'
        state.resto365Users.value = action.payload
      })
      .addCase(gyg365UserListed.rejected, (state, action) => {
        state.resto365Users.loadingState = 'error'
        state.resto365Users.error = action.error
      })
      .addCase(gyg365UserListed.pending, state => {
        state.resto365Users.loadingState = 'pending'
      })
      .addCase(gyg365UserListedCount.pending, state => {
        state.resto365Users.loadingState = 'pending'
      })
      .addCase(gyg365UserListedCount.fulfilled, (state, action) => {
        state.resto365Users.loadingState = 'success'
        state.resto365Users.count = action.payload || 0
      })
      .addCase(gyg365UserListedCount.rejected, (state, action) => {
        state.resto365Users.loadingState = 'error'
        state.resto365Users.error = action.error
      })
      .addCase(checkRoleDuplicate.pending, state => {
        state.roleDuplicateCheck.loadingState = 'pending'
      })
      .addCase(checkRoleDuplicate.fulfilled, (state, action) => {
        state.roleDuplicateCheck.loadingState = 'success'
        state.roleDuplicateCheck.value = action.payload
      })
      .addCase(checkRoleDuplicate.rejected, (state, action) => {
        state.roleDuplicateCheck.loadingState = 'error'
        state.roleDuplicateCheck.error = action.error
      })
      .addCase(fetchHistoryLogs.pending, state => {
        state.historyLogs.loadingState = 'pending'
      })
      .addCase(fetchHistoryLogs.fulfilled, (state, action) => {
        state.historyLogs.loadingState = 'success'
        state.historyLogs.value = action.payload.data
        state.historyLogsMeta = action.payload.meta
      })
      .addCase(fetchHistoryLogs.rejected, (state, action) => {
        state.historyLogs.loadingState = 'error'
        state.historyLogs.error = action.error
      })
      .addCase(exportHistoryLogs.pending, state => {
        state.historyLogsExport.loadingState = 'pending'
      })
      .addCase(exportHistoryLogs.fulfilled, (state, action) => {
        state.historyLogsExport.loadingState = 'success'
        state.historyLogsExport.value = action.payload.data
      })
      .addCase(exportHistoryLogs.rejected, (state, action) => {
        state.historyLogsExport.loadingState = 'error'
        state.historyLogsExport.error = action.error
      })
      .addCase(fetchAllRestaurantScopeUsers.pending, state => {
        state.allRestaurantScopeUsers.loadingState = 'pending'
      })
      .addCase(fetchAllRestaurantScopeUsers.fulfilled, (state, action) => {
        state.allRestaurantScopeUsers.loadingState = 'success'
        state.allRestaurantScopeUsers.value = action.payload
      })
      .addCase(fetchAllRestaurantScopeUsers.rejected, (state, action) => {
        state.allRestaurantScopeUsers.loadingState = 'error'
        state.allRestaurantScopeUsers.error = action.error
      })
  }
})

export const {
  logout,
  invalidateRoles,
  clearCreatedRole,
  clearCreatedUser,
  clearUpdatedRole,
  clearUpdatingRole,
  clearUpdatedUser,
  clearUpdatingUser,
  clearOktaUsers,
  invalidateResto365Users,
  clearResto365Users,
  clearRoleDuplicateCheck,
  clearRolePermissions,
  clearUserAuditLogs,
  clearHistoryLogs,
  clearHistoryLogsExport
} = resto365UserManagement.actions

export default resto365UserManagement.reducer
